// MemoriesJournal — the remembrance journal (WU-W3, character-guided profiling).
//
// A self-contained, no-props (optionally-observable) profile section that lists
// the signed-in user's CONFIRMED UserMemory rows as collectible "petals",
// grouped by the NPC who asked (npcId === null memories collapse into a single
// trailing "The world remembers" group). Each petal can be burned — a
// confirmed→burned STATUS TRANSITION via PUT (never a DELETE, L-AUDIT) that
// makes the backend recompute the caller's trait profile and drop the row from
// the confirmed set. Shapes are imported read-only from the WU-W0 foundation
// (types/memory.ts + services/api/memoryService.ts). See
// requirements/WU-W3-memories-journal.md + 00-FROZEN-CONTRACT.md v1.2.

import { useEffect, useMemo, useState } from 'react';
import type { UserMemory } from '../../types/memory';
import { TRAIT_POLES, TAG_DIMENSION_MAP } from '../../types/memory';
import { memoryService } from '../../services/api/memoryService';
import './MemoriesJournal.css';

const WORLD_GROUP_KEY = '__world__';

interface MemoryGroup {
  key: string;
  label: string;
  isWorld: boolean;
  memories: UserMemory[];
}

interface MemoriesJournalProps {
  /**
   * OPTIONAL. Fired after a successful burn (which recomputes traits
   * server-side) so a parent surface — e.g. WU-W1's "world knows you" ring —
   * can refetch GET /api/profile/traits. The default mount stays props-less
   * (`<MemoriesJournal />`); the ring is never a hard dependency.
   */
  onMemoriesChanged?: () => void;
}

/**
 * Group label for an NPC-sourced memory: the joined npcName, else the
 * dimension's NPC lens (TRAIT_POLES), else a neutral fallback. Never touches a
 * null NPC (those route to the world bucket before this is called).
 */
function npcGroupLabel(m: UserMemory): string {
  if (m.npcName && m.npcName.trim()) return m.npcName.trim();
  if (m.dimension && TRAIT_POLES[m.dimension]) return TRAIT_POLES[m.dimension].npc;
  return 'The world';
}

/** First-letter initials for an avatar chip (e.g. "Old Tomas" → "OT"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '✦';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Render a controlled-vocab tag as a human phrase ("corner_booth" → "corner booth"). */
function humanize(value: string): string {
  return value.replace(/_/g, ' ');
}

/** Presentation for a memory's polarity glyph + its accessible label. */
function polarityMeta(polarity: -1 | 0 | 1): { cls: string; glyph: string; label: string } {
  if (polarity === 1) return { cls: 'affinity', glyph: '♥', label: 'Something you love' };
  if (polarity === -1) return { cls: 'aversion', glyph: '⊘', label: 'Something you steer clear of' };
  return { cls: 'neutral', glyph: '•', label: 'A neutral fact' };
}

export function MemoriesJournal({ onMemoriesChanged }: MemoriesJournalProps = {}) {
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [burningId, setBurningId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  // Fetch the confirmed set on mount (already newest-first from the API).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    memoryService
      .getMemories({ status: 'confirmed' })
      .then((rows) => {
        if (cancelled) return;
        setMemories(rows);
      })
      .catch(() => {
        if (!cancelled) setMemories([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bucket by NPC, preserving newest-first order within each group; groups are
  // ordered by their most-recent memory (Map insertion order), with the
  // null-NPC "The world remembers" group always trailing.
  const groups = useMemo<MemoryGroup[]>(() => {
    const npcGroups = new Map<string, MemoryGroup>();
    const world: UserMemory[] = [];

    for (const m of memories) {
      if (m.npcId === null || m.npcId === undefined) {
        world.push(m);
        continue;
      }
      const existing = npcGroups.get(m.npcId);
      if (existing) {
        existing.memories.push(m);
      } else {
        npcGroups.set(m.npcId, {
          key: m.npcId,
          label: npcGroupLabel(m),
          isWorld: false,
          memories: [m],
        });
      }
    }

    const ordered: MemoryGroup[] = Array.from(npcGroups.values());
    if (world.length > 0) {
      ordered.push({
        key: WORLD_GROUP_KEY,
        label: 'The world remembers',
        isWorld: true,
        memories: world,
      });
    }
    return ordered;
  }, [memories]);

  // Burn = optimistic remove + revert on the null/failure signal (L-AUDIT: a
  // status transition, never a DELETE).
  async function handleBurn(id: string) {
    const snapshot = memories;
    setError(null);
    setConfirmingId(null);
    setBurningId(id);
    setMemories((prev) => prev.filter((m) => m.id !== id)); // optimistic
    try {
      const result = await memoryService.updateMemoryStatus(id, 'burned');
      if (result) {
        onMemoriesChanged?.();
      } else {
        setMemories(snapshot); // revert on null
        setError('That memory did not want to be forgotten — it is still here. Try again in a moment.');
      }
    } catch {
      setMemories(snapshot); // revert on unexpected throw
      setError('Something interrupted the burn — that memory is still here. Try again in a moment.');
    } finally {
      setBurningId(null);
    }
  }

  return (
    <div className="memories-journal">
      <header className="mj-header">
        <h3 className="mj-title">What the world remembers about you</h3>
        <p className="mj-subtitle">
          Every character you talk to keeps a little memory. They bloom here — burn any you would
          rather were forgotten.
        </p>
      </header>

      {error && (
        <div className="mj-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mj-loading" aria-busy="true" aria-live="polite">
          {[0, 1, 2].map((i) => (
            <div key={i} className="memory-petal memory-petal--skeleton" />
          ))}
          <span className="mj-visually-hidden">Gathering what the world remembers…</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="mj-empty">
          <span className="mj-empty-glyph" aria-hidden="true">
            ✦
          </span>
          <p className="mj-empty-title">The world doesn&apos;t know you yet</p>
          <p className="mj-empty-body">
            Go wander and chat with the characters — the ones you open up to will start to remember
            you, and their memories will bloom right here.
          </p>
        </div>
      ) : (
        <div className="mj-groups">
          {groups.map((group) => (
            <section
              key={group.key}
              className={`memory-npc-group${group.isWorld ? ' memory-npc-group--world' : ''}`}
            >
              <header className="memory-group-header">
                <span className="memory-group-avatar" aria-hidden="true">
                  {group.isWorld ? '✦' : initialsOf(group.label)}
                </span>
                <h4 className="memory-group-name">{group.label}</h4>
                <span
                  className="memory-group-count"
                  aria-label={`${group.memories.length} ${
                    group.memories.length === 1 ? 'memory' : 'memories'
                  }`}
                >
                  {group.memories.length}
                </span>
              </header>

              <div className="memory-petals">
                {group.memories.map((m) => {
                  const pol = polarityMeta(m.polarity);
                  const isConfirming = confirmingId === m.id;
                  const isBurning = burningId === m.id;
                  return (
                    <article key={m.id} className="memory-petal">
                      <div className="memory-petal-head">
                        <span
                          className={`memory-polarity memory-polarity--${pol.cls}`}
                          title={pol.label}
                        >
                          <span aria-hidden="true">{pol.glyph}</span>
                          <span className="mj-visually-hidden">{pol.label}</span>
                        </span>
                        <p className="memory-text">{m.text}</p>
                      </div>

                      {m.tags.length > 0 && (
                        <div className="memory-tags">
                          {m.tags.map((tag) => {
                            const vocab = TAG_DIMENSION_MAP[tag];
                            return (
                              <span
                                key={tag}
                                className="memory-tag-chip"
                                title={vocab ? humanize(vocab.dimension) : undefined}
                              >
                                {humanize(tag)}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      <div className="memory-petal-actions">
                        {isConfirming ? (
                          <div
                            className="memory-confirm"
                            role="group"
                            aria-label="Confirm burning this memory"
                          >
                            <span className="memory-confirm-q">Let it fade for good?</span>
                            <button
                              type="button"
                              className="memory-burn-btn memory-burn-btn--confirm"
                              onClick={() => handleBurn(m.id)}
                              disabled={isBurning}
                            >
                              {isBurning ? 'Fading…' : 'Burn it 🔥'}
                            </button>
                            <button
                              type="button"
                              className="memory-keep-btn"
                              onClick={() => setConfirmingId(null)}
                              disabled={isBurning}
                            >
                              Keep it
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="memory-burn-btn"
                            onClick={() => {
                              setError(null);
                              setConfirmingId(m.id);
                            }}
                          >
                            Burn this memory 🔥
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export default MemoriesJournal;
